import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { usePostApiV1IndividualpaymentFinancialPeopleFinancialpersonidInactivate } from '@/shared/api/generated';

/** Rota da listagem de pessoas, cujo cache é invalidado após a inativação. */
const PEOPLE_QUERY_URL = '/api/v1/individualpayment/people';
/** Rota dos contratos de uma pessoa — a linha expandida da listagem. */
const COURSES_QUERY_URL = '/api/v1/individualpayment/people/:personId/courses';

/**
 * Inativação de um plano financeiro a partir da listagem.
 *
 * Não reusa `useParcelActionRunner`, que roda a mesma mutation na tela de
 * parcelas: aquele hook invalida `financial-people/{id}/parcels` e `/header`, que
 * são as queries **daquela** tela. Aqui o que precisa voltar do servidor é a
 * lista de contratos da pessoa — é ela que mostra o status que acabou de mudar.
 * Reusar o outro hook deixaria a linha exibindo o contrato ainda ativo.
 *
 * O endpoint faz mais do que mudar um status: cancela as parcelas abertas
 * (aguardando integração e pendentes) junto com a inativação. Daí a confirmação
 * ser obrigatória em quem chama.
 *
 * @param personId - Dono dos contratos, para invalidar a linha certa; `null`
 *   torna o `inactivate` inerte.
 * @returns `{ inactivate, isPending, error, reset }`
 *
 * @example
 * ```tsx
 * const { inactivate, isPending } = useInactivateFinancial(personId);
 * inactivate(financialPersonId, { onSuccess: () => setModalOpen(false) });
 * ```
 */
export function useInactivateFinancial(personId: number | null) {
  const queryClient = useQueryClient();
  const mutation = usePostApiV1IndividualpaymentFinancialPeopleFinancialpersonidInactivate();

  const inactivate = useCallback(
    (financialPersonId: number, options?: { readonly onSuccess?: () => void }) => {
      if (personId === null) return;

      mutation.mutate(
        { financialPersonId },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey: [{ url: COURSES_QUERY_URL, params: { personId } }],
            });
            void queryClient.invalidateQueries({ queryKey: [{ url: PEOPLE_QUERY_URL }] });
            options?.onSuccess?.();
          },
        },
      );
    },
    [personId, mutation, queryClient],
  );

  return {
    inactivate,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  } as const;
}
